import { Handshake, Percent } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { formatRON } from "@/lib/api";

function statusBadgeClass(status) {
  if (status === "closed") return "bg-slate-400";
  if (status === "contacted") return "bg-blue-600";
  return "bg-green-600";
}

function urgencyClass(urgency) {
  if (urgency === "high") return "bg-red-600 text-white";
  if (urgency === "medium") return "bg-amber-500 text-slate-900";
  return "bg-slate-200 text-slate-700";
}

export default function AdminConsultations({
  consultations,
  newConsultations,
  stats,
  commission,
  setCommission,
  saveCommission,
  feeInputs,
  setFeeInputs,
  saveFee,
  copyPayLink,
  togglePayout,
  setConsultStatus,
}) {
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <div>
          <h2 className="font-bold text-lg">Cereri de consultanță ({consultations.length}) · {newConsultations} noi</h2>
          <p className="text-sm text-slate-500 mt-0.5">Comision OXO încasat: <span className="font-black text-orange-600" data-testid="collected-commission">{formatRON(stats?.collected_commission || 0)}</span></p>
        </div>
        <div className="flex items-end gap-2" data-testid="commission-setting">
          <div>
            <label className="text-xs uppercase tracking-wide font-bold text-slate-500 block mb-1">Comision OXO (%)</label>
            <Input type="number" value={commission} onChange={(e) => setCommission(e.target.value)} className="rounded-none w-28" data-testid="commission-input" />
          </div>
          <button onClick={saveCommission} className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 text-sm font-bold uppercase transition-colors h-10" data-testid="save-commission"><Percent className="w-4 h-4" /> Salvează</button>
        </div>
      </div>
      <div className="space-y-3">
        {consultations.length === 0 && <div className="border border-slate-200 p-8 text-center text-slate-400">Nicio cerere de consultanță.</div>}
        {consultations.map((c) => (
          <div key={c.id} className="border border-slate-200 p-5" data-testid={`admin-consultation-${c.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 font-semibold">
                <Handshake className="w-4 h-4 text-orange-600" /> {c.company}
                <span className="text-slate-400 font-normal">· {c.contact_name}</span>
                <span className={`text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 ${urgencyClass(c.urgency)}`}>{c.issue_type}</span>
              </div>
              <Badge className={`rounded-none ${statusBadgeClass(c.status)}`}>{c.status}</Badge>
            </div>
            <div className="text-xs text-slate-500 mt-1">Pentru expert: <span className="font-semibold text-slate-700">{c.expert_name}</span> · {c.contact_email}{c.contact_phone ? ` · ${c.contact_phone}` : ""}</div>
            <p className="text-sm text-slate-600 mt-2">{c.project_description}</p>
            <div className="flex flex-wrap items-center gap-4 mt-3 text-sm">
              <span className="text-slate-500">Buget: <span className="font-bold text-slate-900">{c.estimated_fee ? formatRON(c.estimated_fee) : "—"}</span></span>
              <span className="text-slate-500">Comision OXO ({c.commission_pct}%): <span className="font-bold text-orange-600" data-testid={`commission-amount-${c.id}`}>{c.commission_amount != null ? formatRON(c.commission_amount) : "—"}</span></span>
              <span className="text-xs text-slate-400 ml-auto">{new Date(c.created_at).toLocaleString("ro-RO")}</span>
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={() => setConsultStatus(c.id, "contacted")} className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 text-xs font-bold uppercase transition-colors" data-testid={`consult-contacted-${c.id}`}>Contactat</button>
              <button onClick={() => setConsultStatus(c.id, "closed")} className="bg-slate-700 hover:bg-slate-800 text-white px-3 py-1.5 text-xs font-bold uppercase transition-colors" data-testid={`consult-closed-${c.id}`}>Închide</button>
              {c.status !== "new" && <button onClick={() => setConsultStatus(c.id, "new")} className="border border-slate-300 hover:border-slate-900 px-3 py-1.5 text-xs font-bold uppercase transition-colors" data-testid={`consult-reopen-${c.id}`}>Redeschide</button>}
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100" data-testid={`escrow-block-${c.id}`}>
              <div className="text-[10px] uppercase tracking-wide font-bold text-slate-400 mb-2">Plată mediată OXO (escrow-lite)</div>
              {c.payment_status === "paid" ? (
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <Badge className="rounded-none bg-green-600">Plătit · {formatRON(c.paid_amount)}</Badge>
                  <span className="text-slate-500">Comision OXO: <span className="font-bold text-orange-600">{formatRON(c.commission_collected)}</span></span>
                  <span className="text-slate-500">Net expert: <span className="font-bold text-slate-900">{formatRON(c.expert_net)}</span></span>
                  <button onClick={() => togglePayout(c.id)} className={`px-3 py-1.5 text-xs font-bold uppercase transition-colors ml-auto ${c.payout_status === "paid_out" ? "bg-slate-200 text-slate-700 hover:bg-slate-300" : "bg-slate-900 text-white hover:bg-slate-800"}`} data-testid={`payout-${c.id}`}>
                    {c.payout_status === "paid_out" ? "✓ Expert plătit (anulează)" : "Marchează expert plătit"}
                  </button>
                </div>
              ) : (
                <div className="flex flex-wrap items-end gap-2">
                  <div>
                    <label className="text-xs text-slate-500 block mb-1">Tarif convenit (RON)</label>
                    <Input type="number" defaultValue={c.agreed_fee || ""} onChange={(e) => setFeeInputs({ ...feeInputs, [c.id]: e.target.value })} className="rounded-none w-36 h-9" data-testid={`agreed-fee-${c.id}`} />
                  </div>
                  <button onClick={() => saveFee(c)} className="bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 text-xs font-bold uppercase transition-colors h-9" data-testid={`save-fee-${c.id}`}>Salvează tarif</button>
                  {c.agreed_fee ? (
                    <button onClick={() => copyPayLink(c)} className="border border-orange-600 text-orange-600 hover:bg-orange-50 px-3 py-1.5 text-xs font-bold uppercase transition-colors h-9" data-testid={`pay-link-${c.id}`}>Copiază link plată</button>
                  ) : null}
                  {c.agreed_fee ? <span className="text-xs text-slate-400 self-center">Comision estimat: {formatRON((c.agreed_fee * (c.commission_pct || 10)) / 100)}</span> : null}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
